import { defineMessages, IntlShape } from 'react-intl';
import { isReviewableResourceType } from '../../util/extractedResourceReview';
import type { MemberExtractionResourceRow } from './useMemberExtractionDocuments';

const messages = defineMessages({
  labCount: {
    id: 'phc-web-toolkit.memberExtractions.typeCount.lab',
    defaultMessage: '{count, plural, one {Lab} other {Labs}}',
  },
  conditionCount: {
    id: 'phc-web-toolkit.memberExtractions.typeCount.condition',
    defaultMessage: '{count, plural, one {Condition} other {Conditions}}',
  },
  medicationCount: {
    id: 'phc-web-toolkit.memberExtractions.typeCount.medication',
    defaultMessage: '{count, plural, one {Medication} other {Medications}}',
  },
  allergyCount: {
    id: 'phc-web-toolkit.memberExtractions.typeCount.allergy',
    defaultMessage: '{count, plural, one {Allergy} other {Allergies}}',
  },
  immunizationCount: {
    id: 'phc-web-toolkit.memberExtractions.typeCount.immunization',
    defaultMessage: '{count, plural, one {Immunization} other {Immunizations}}',
  },
  procedureCount: {
    id: 'phc-web-toolkit.memberExtractions.typeCount.procedure',
    defaultMessage: '{count, plural, one {Procedure} other {Procedures}}',
  },
  otherCount: {
    id: 'phc-web-toolkit.memberExtractions.typeCount.other',
    defaultMessage: '{type}',
  },
  labGroup: {
    id: 'phc-web-toolkit.memberExtractions.group.lab',
    defaultMessage: 'Labs',
  },
  conditionGroup: {
    id: 'phc-web-toolkit.memberExtractions.group.condition',
    defaultMessage: 'Conditions',
  },
  medicationGroup: {
    id: 'phc-web-toolkit.memberExtractions.group.medication',
    defaultMessage: 'Medications',
  },
  allergyGroup: {
    id: 'phc-web-toolkit.memberExtractions.group.allergy',
    defaultMessage: 'Allergies',
  },
  immunizationGroup: {
    id: 'phc-web-toolkit.memberExtractions.group.immunization',
    defaultMessage: 'Immunizations',
  },
  procedureGroup: {
    id: 'phc-web-toolkit.memberExtractions.group.procedure',
    defaultMessage: 'Procedures',
  },
});

type FormatMessage = IntlShape['formatMessage'];

export const RESOURCE_GROUP_COLOR: Record<string, string> = {
  Observation: '#558fbe',
  Condition: '#bc5c5c',
  MedicationRequest: '#b68b2b',
  AllergyIntolerance: '#8c56b3',
  Immunization: '#54ab5b',
  Procedure: '#46a4a4',
};

export const DEFAULT_GROUP_COLOR = '#a1a1aa';

const TYPE_COUNTS: Array<{
  types: string[];
  message: keyof typeof messages;
  label: keyof typeof messages;
}> = [
  { types: ['Observation'], message: 'labCount', label: 'labGroup' },
  { types: ['Condition'], message: 'conditionCount', label: 'conditionGroup' },
  {
    types: ['MedicationRequest', 'MedicationStatement'],
    message: 'medicationCount',
    label: 'medicationGroup',
  },
  {
    types: ['AllergyIntolerance'],
    message: 'allergyCount',
    label: 'allergyGroup',
  },
  {
    types: ['Immunization'],
    message: 'immunizationCount',
    label: 'immunizationGroup',
  },
  { types: ['Procedure'], message: 'procedureCount', label: 'procedureGroup' },
];

const resourceGroupKey = (resourceType: string) =>
  TYPE_COUNTS.find(entry => entry.types.includes(resourceType))?.types[0] ??
  resourceType;

export const resourceGroupColor = (key: string) =>
  RESOURCE_GROUP_COLOR[key] ?? DEFAULT_GROUP_COLOR;

export const resourceGroupLabel = (
  key: string,
  formatMessage: FormatMessage,
) => {
  const entry = TYPE_COUNTS.find(item => item.types[0] === key);
  return entry ? formatMessage(messages[entry.label]) : key;
};

export const groupResourcesByType = (
  resources: MemberExtractionResourceRow[],
) => {
  const order: string[] = [];
  const grouped = new Map<string, MemberExtractionResourceRow[]>();
  resources.forEach(resource => {
    const key = resourceGroupKey(resource.resourceType);
    const existing = grouped.get(key);
    if (existing) {
      existing.push(resource);
      return;
    }
    order.push(key);
    grouped.set(key, [resource]);
  });
  return order.map(key => ({
    key,
    resources: grouped.get(key) ?? [],
  }));
};

export const resourceTypeCounts = (
  resources: MemberExtractionResourceRow[],
  formatMessage: FormatMessage,
) => {
  const counts = new Map<string, number>();
  resources.forEach(resource => {
    counts.set(
      resource.resourceType,
      (counts.get(resource.resourceType) ?? 0) + 1,
    );
  });
  const seen = new Set<string>();
  const parts = TYPE_COUNTS.flatMap(entry => {
    entry.types.forEach(type => seen.add(type));
    const count = entry.types.reduce(
      (sum, type) => sum + (counts.get(type) ?? 0),
      0,
    );
    if (count === 0) {
      return [];
    }
    const key = entry.types[0];
    return [
      {
        key,
        count,
        label: formatMessage(messages[entry.message], { count }),
        color: resourceGroupColor(key),
      },
    ];
  });
  counts.forEach((count, type) => {
    if (!seen.has(type) && count > 0) {
      parts.push({
        key: type,
        count,
        label: formatMessage(messages.otherCount, { type }),
        color: DEFAULT_GROUP_COLOR,
      });
    }
  });
  return parts;
};

export const countResourcesToReview = (
  resources: MemberExtractionResourceRow[],
) =>
  resources.filter(
    resource =>
      !resource.reviewDecision &&
      isReviewableResourceType(resource.resourceType),
  ).length;
